// Who can sign in to the crew app for this crew, with the two things ops
// keeps needing when a crew says "I can't get in": send them the reset mail,
// and fix the address it goes to.
//
// The login email is NOT the crew's contact email edited in the Identity
// section above. That one feeds booking notifications; this one is only
// where a person signs in. Keeping the edit here, on the person, is what
// stops "fix the reset inbox" from silently rerouting a crew's bookings.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailSection, StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import {
  fetchAdminVendorLogins,
  sendCrewLoginReset,
  updateCrewLoginEmail,
  type AdminCrewLogin,
} from '@/lib/admin-api';

/** The API's error codes, in words ops can act on. Exported for the test. */
export function describeLoginError(err: unknown, action: 'reset' | 'email'): string {
  const code = err instanceof ApiError ? (err.body as { error?: string } | undefined)?.error : undefined;
  switch (code) {
    case 'email_taken':
      return 'Another crew login already uses that email.';
    case 'account_suspended':
      return 'This login is suspended. Unsuspend the crew before sending a reset.';
    case 'email_failed':
      return 'The reset email could not be sent. Check the address, then try again.';
    case 'invalid_payload':
      return 'That is not a valid email address.';
    case 'not_found':
      return 'That login no longer exists.';
    default:
      return action === 'reset' ? 'Could not send the reset email.' : 'Could not change the email.';
  }
}

function displayName(l: AdminCrewLogin): string {
  return [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email;
}

function flags(l: AdminCrewLogin): string {
  const parts = [l.role];
  if (l.lockedUntil) parts.push('locked after failed attempts');
  if (l.resetPending) parts.push('reset link pending');
  if (l.twoFactorEnabled) parts.push('2FA on');
  if (!l.verified) parts.push('email unverified');
  return parts.join(' · ');
}

export function VendorLoginsSection({ vendorId }: { vendorId: number }) {
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'vendors', vendorId, 'logins'];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchAdminVendorLogins(vendorId, signal),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey });

  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<{ id: number; email: string } | null>(null);

  const resetMut = useMutation({
    mutationFn: (crewUserId: number) => sendCrewLoginReset(crewUserId),
    onSuccess: (r) => {
      setNotice({ kind: 'ok', text: `Reset email sent to ${r.sentTo}. The link works for one hour.` });
      invalidate();
    },
    onError: (err) => setNotice({ kind: 'error', text: describeLoginError(err, 'reset') }),
  });
  const emailMut = useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) => updateCrewLoginEmail(id, email),
    onSuccess: (r) => {
      setNotice({
        kind: 'ok',
        text: r.changed ? `Login email changed to ${r.email}.` : 'That was already the login email.',
      });
      setEditing(null);
      invalidate();
    },
    onError: (err) => setNotice({ kind: 'error', text: describeLoginError(err, 'email') }),
  });

  const submitEmail = (e?: FormEvent) => {
    e?.preventDefault();
    if (!editing || emailMut.isPending) return;
    emailMut.mutate({ id: editing.id, email: editing.email.trim() });
  };

  const rows = data?.logins ?? [];

  return (
    <DetailSection title={`Logins (${rows.length})`}>
      <p className="mb-3 text-[12.5px] text-[color:var(--bo-mute)]">
        Who can sign in to the crew app for this crew. A login&apos;s email is where its password-reset
        link goes; it is separate from the crew&apos;s contact email above, so change it here when a reset
        needs to reach a different inbox.
      </p>
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load logins: {(error as Error).message}
        </div>
      ) : null}
      {notice ? (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={
            notice.kind === 'error'
              ? 'mb-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]'
              : 'mb-3 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2 text-[12.5px] text-[#166534]'
          }
        >
          {notice.text}
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-[12.5px] text-[color:var(--bo-mute)]">
          {isLoading ? 'Loading…' : 'Nobody can sign in to this crew yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--bo-line)]">
          {rows.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`login-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-[color:var(--bo-ink)]">{displayName(l)}</div>
                {editing?.id === l.id ? (
                  <form onSubmit={submitEmail} className="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      type="email"
                      required
                      autoFocus
                      aria-label="New login email"
                      value={editing.email}
                      onChange={(e) => setEditing({ id: l.id, email: e.target.value })}
                      className="h-8 min-w-[16rem] rounded-md border border-[color:var(--bo-line)] px-2 text-[12.5px]"
                    />
                    <RowAction kind="primary" busy={emailMut.isPending} onClick={() => submitEmail()}>
                      Save email
                    </RowAction>
                    <RowAction onClick={() => setEditing(null)}>Cancel</RowAction>
                  </form>
                ) : (
                  <div className="text-[12.5px] text-[color:var(--bo-mute)]">{l.email}</div>
                )}
                <div className="text-[11.5px] text-[color:var(--bo-mute)]">{flags(l)}</div>
              </div>
              <StatusPill status={l.status} />
              <RowAction
                busy={resetMut.isPending && resetMut.variables === l.id}
                onClick={() => {
                  setNotice(null);
                  resetMut.mutate(l.id);
                }}
              >
                Send reset email
              </RowAction>
              {editing?.id === l.id ? null : (
                <RowAction
                  onClick={() => {
                    setNotice(null);
                    setEditing({ id: l.id, email: l.email });
                  }}
                >
                  Change email
                </RowAction>
              )}
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}
